import * as Yup from 'yup';
import Honeybadger from '@honeybadger-io/js';

import { getCdtCodes } from 'api/CdtCodes';
import { getCptCodes } from 'api/CptCodes';

import { Capitalize } from 'lib/helpers/utility';
import { serviceChargeTypes } from 'lib/constants/constants';

const ZERO_VALUE = 0;
const DEFAULT_TIME_LENGTH = 60;
const DEFAULT_CHARGE_TYPE = {
  value: 'self_pay',
  label: 'Self Pay'
};

const findServiceChargeType = (value) => {
  return serviceChargeTypes.find((chargeType) => chargeType.value === value);
};

const getNdcCodes = (serviceNdc) => {
  return serviceNdc.flatMap((item) =>
    item.ndc_codes.map((ndc) => ({
      value: ndc?.package_ndc,
      label: ndc?.package_ndc,
      title: `[ ${Capitalize(ndc?.generic_name)} ]\n ${ndc?.packaging_description}`
    }))
  );
};

export const getInitialValues = (serviceToBeUpdated) => ({
  name: serviceToBeUpdated?.name || '',
  description: serviceToBeUpdated?.description || '',
  image: serviceToBeUpdated?.image || '',
  procedure_code: serviceToBeUpdated?.procedure_code || '',
  par_amount:
    serviceToBeUpdated?.cpt?.par_amount ?? serviceToBeUpdated?.cdt?.par_amount ?? ZERO_VALUE,
  total_cost_cents: serviceToBeUpdated?.total_cost_cents ?? ZERO_VALUE,
  time_length: serviceToBeUpdated?.time_length ?? DEFAULT_TIME_LENGTH,
  self_scheduling: serviceToBeUpdated?.self_scheduling ?? false,
  charge_type: serviceToBeUpdated?.charge_type
    ? findServiceChargeType(serviceToBeUpdated?.charge_type)
    : DEFAULT_CHARGE_TYPE,
  standard_charge: serviceToBeUpdated?.standard_charge ?? ZERO_VALUE,
  cpt: serviceToBeUpdated?.cpt || null,
  cdt: serviceToBeUpdated?.cdt || null,
  dental_category: serviceToBeUpdated?.dental_category || null,
  paySchedule: serviceToBeUpdated?.payerConnection || [],
  serviceId: serviceToBeUpdated?.id || null,
  practitioner_ids: serviceToBeUpdated?.practitioner_ids || [],
  color: serviceToBeUpdated?.color,
  category_id: serviceToBeUpdated?.category_id || null,
  state_tax_rate: serviceToBeUpdated?.state_tax_rate ?? ZERO_VALUE,
  local_tax_rate: serviceToBeUpdated?.local_tax_rate ?? ZERO_VALUE,
  ndc_codes: getNdcCodes(serviceToBeUpdated?.serviceNdc || [])
});

export const getServiceValidationSchema = (isDental) => {
  const shape = {
    name: Yup.string().required('Name is required'),
    total_cost_cents: Yup.number().required('Price is required'),
    charge_type: Yup.object().required('Charge type is required'),
    time_length: Yup.number().required('Time length is required'),
    self_scheduling: Yup.boolean(),
    // A self-scheduling service with no practitioners can't be booked by patients,
    // so require at least one here (the backend enforces this too).
    practitioner_ids: Yup.array().when('self_scheduling', {
      is: true,
      then: (schema) =>
        schema.min(1, 'Select at least one provider to enable self-scheduling for this service'),
      otherwise: (schema) => schema.nullable()
    })
  };

  // Spec §3.2: dental_category is required for dental services. Mirrors the
  // backend `validateDentalCategory` check so users get inline feedback.
  if (isDental) {
    shape.dental_category = Yup.string()
      .nullable()
      .required('Service category is required for dental services');
  }

  return Yup.object().shape(shape);
};

export const getFinalShapeServiceObj = (values) => ({
  name: values.name,
  description: values.description,
  procedure_code: values.procedure_code,
  total_cost_cents: Number(values.total_cost_cents),
  time_length: values.time_length,
  self_scheduling: values.self_scheduling,
  charge_type: values.charge_type.value,
  par_amount: Number(values.par_amount),
  standard_charge: Number(values.standard_charge),
  image: values.image,
  practitioner_ids: values.practitioner_ids,
  color: values.color || '#0BA5EC',
  category_id: values.category_id,
  state_tax_rate: values.state_tax_rate,
  local_tax_rate: values.local_tax_rate,
  dental_category: values.dental_category || null,
  ndc_codes: values.ndc_codes.map((item) => item.value)
});

const formatCptCode = (item) => ({
  value: item.code,
  label: `${item?.code} - ${item?.name || 'N/A'}`,
  par_amount: item.par_amount || 0,
  meta: item,
  base_unit: item.base_unit
});

const formatCdtCode = (item) => ({
  value: item.code,
  label: `${item?.code} - ${item?.name || 'N/A'}`,
  description: item?.name || '',
  par_amount: item.par_amount || 0,
  dental_category: item.dental_category || null,
  meta: item
});

const DEFAULT_LIMIT = 100;

export const handleCPTSearch = async (searchTerm, navigate) => {
  if (!searchTerm) {
    return [];
  }

  try {
    const params = {
      searchTerm,
      limit: DEFAULT_LIMIT
    };

    const data = await getCptCodes(navigate, params);
    const cptCodes = data?.cpt;

    if (!cptCodes) {
      throw new Error('No CPT® codes returned from API');
    }

    return cptCodes.map(formatCptCode);
  } catch (error) {
    const errorMessage = `Failed to search CPT® codes: ${error.message}`;
    Honeybadger.notify(errorMessage);
    return [];
  }
};

export const handleCDTSearch = async (searchTerm, navigate) => {
  if (!searchTerm) {
    return [];
  }

  try {
    const params = {
      searchTerm,
      limit: DEFAULT_LIMIT
    };

    const data = await getCdtCodes(navigate, params);
    const cdtCodes = data?.cdt;

    if (!cdtCodes) {
      throw new Error('No CDT codes returned from API');
    }

    return cdtCodes.map(formatCdtCode);
  } catch (error) {
    const errorMessage = `Failed to search CDT codes: ${error.message}`;
    Honeybadger.notify(errorMessage);
    return [];
  }
};
